import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectIsMeLoaded, selectUser } from '../../reducers/user.selectors';
import useRouter from '../../utils/useRouter';

interface Options {
	/**
	 * The gate: `true` = the page requires a Stripe user (redirect Zuora users
	 * away); `false` = the page requires a non-Stripe user (redirect Stripe
	 * users away).
	 */
	mustBeStripe: boolean;
	/** Where to send the user when their `isStripeUser` state doesn't match. */
	redirectTo: string;
}

/**
 * Route-level gate for the two teachers pages. When the current user's
 * `isStripeUser` flag (server-computed on `/me` by the legacy proxy — see
 * `apps/docs/content/guides/school-teacher-invitations.md`) does not match
 * `mustBeStripe`, replaces the current URL with `redirectTo`.
 *
 * Waits for `me` to finish loading before deciding, so a slow `/me` never
 * bounces the user off a legitimate URL. Uses `router.replace` (not `push`)
 * so the wrong URL never lands in browser history.
 *
 * Gates on `selectIsMeLoaded` (`!!state.user.me`) rather than the shared
 * `state.user.status` — the shared status is toggled by unrelated async
 * flows (e.g. `GET_SCHOOL_SEATS.START`), which would otherwise unmount the
 * child mid-fetch and cause a mount/remount loop of the underlying page.
 */
export default function useRequireStripeUser({ mustBeStripe, redirectTo }: Options) {
	const isReady = useSelector(selectIsMeLoaded);
	const user = useSelector(selectUser);
	const { router } = useRouter();

	const matches = !!user?.isStripeUser === mustBeStripe;

	useEffect(() => {
		if (isReady && !matches) {
			router.replace(redirectTo);
		}
	}, [router, isReady, matches, redirectTo]);

	return { isReady, matches };
}
